<script setup>
import { computed, ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { IconHome, IconQrcode, IconUserCircle, IconDotsCircleHorizontal } from '@tabler/icons-vue'
import { useBottomNavStore } from '@/stores/bottomNavStore'

const route = useRoute()
const bottomNavStore = useBottomNavStore()

const menuItems = computed(() => [
  {
    route: '/hub',
    icon: IconHome,
    label: __DICT__.hub?.hubTab || 'Hub',
    isActive: route.path === '/hub'
  },
  {
    route: '/scan',
    icon: IconQrcode,
    label: __DICT__.bottomMenu.scan,
    isActive: route.path === '/scan'
  },
  {
    route: '/profile',
    icon: IconUserCircle,
    label: __DICT__.hub?.profile || 'Profile',
    isActive: route.path === '/profile'
  },
  {
    route: '/more',
    icon: IconDotsCircleHorizontal,
    label: __DICT__.hub?.more || 'More',
    isActive: route.path === '/more'
  }
])

const isBottomNavRoute = computed(() => menuItems.value.some(item => item.isActive))

// Simplified state tracking
const previousIndex = ref(-1)
const currentIndex = ref(-1)

// Initialize indices
onMounted(() => {
  const initialIndex = menuItems.value.findIndex(item => item.isActive)
  currentIndex.value = initialIndex
  previousIndex.value = initialIndex
})

// Watch for changes in the route
watch(() => route.path, (newPath) => {
  const newIndex = menuItems.value.findIndex(item => item.route === newPath)
  if (newIndex !== -1) {
    previousIndex.value = currentIndex.value
    currentIndex.value = newIndex
  }
})

const backgroundStyle = computed(() => {
  const itemWidth = 100 / menuItems.value.length
  const backgroundWidth = itemWidth * 0.8
  const offset = (itemWidth - backgroundWidth) / 2

  // Use current index for position when on a bottom nav route
  const indexToUse = isBottomNavRoute.value ? currentIndex.value : previousIndex.value
  const horizontalPosition = itemWidth * indexToUse + offset

  return {
    width: `${backgroundWidth}%`,
    left: `${horizontalPosition}%`,
    opacity: isBottomNavRoute.value ? 1 : 0,
    transform: isBottomNavRoute.value ? 'translateY(0)' : 'translateY(100%)',
    transition: hasLoaded.value
        ? 'left 0.3s ease, width 0.3s ease, opacity 0.3s ease, transform 0.3s ease'
        : 'none'
  }
})

const hasLoaded = ref(false)

onMounted(() => {
  setTimeout(() => {
    hasLoaded.value = true
  }, 50)
})

const navStyle = computed(() => ({
  transform: bottomNavStore.showBottomNav ? 'translateY(0)' : 'translateY(100%)',
  transition: 'transform 0.3s ease'
}))
</script>

<template>
  <nav class="navbar fixed-bottom navbar-expand p-0" :style="navStyle">
    <div class="container-fluid text-center position-relative">
      <div class="menu-background" :style="backgroundStyle"></div>
      <router-link
          v-for="(item, index) in menuItems"
          :key="index"
          :to="item.route"
          class="nav-link d-flex flex-column align-items-center justify-content-center menu-item"
          :class="{ 'active': item.isActive }"
      >
        <component :is="item.icon" :width="item.isActive ? 26 : 24" :height="item.isActive ? 26 : 24"/>
        <span v-if="!item.isActive" class="menu-label">{{ item.label }}</span>
      </router-link>
    </div>
  </nav>
</template>

<style scoped>
.navbar {
  height: 64px;
  background-color: var(--tblr-bg-surface);
  box-shadow: 0 -2px 10px rgba(var(--tblr-body-color-rgb), 0.05);
}

.container-fluid {
  padding: 0;
  height: 100%;
}

.menu-item {
  flex: 1;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  padding: 8px 0 !important;
  color: rgba(var(--tblr-body-color-rgb), 0.6);
}

.menu-item:hover {
  background-color: rgba(var(--tblr-body-color-rgb), 0.04);
}

.menu-label {
  font-size: 0.75rem;
  font-weight: 500;
  margin-top: 4px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center bottom;
}

.menu-item:not(.active) .menu-label {
  opacity: 0.7;
}

.menu-item.active .menu-label {
  opacity: 1;
  transform: scale(1.05);
}

.menu-background {
  position: absolute;
  height: 40px;
  top: 12px;
  background-color: var(--tblr-primary);
  border-radius: 16px;
  z-index: 0;
  box-shadow: 0 2px 8px rgba(var(--tblr-body-color-rgb), 0.15);
  transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.menu-item {
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.menu-item.active {
  color: white;
}

.menu-item svg {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.menu-item.active svg {
  transform: translateY(0);
  margin-top: 4px;
}
</style>
